/** Tab F — Communications (expanded) report. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useDrillThrough } from "@/hooks/useDrillThrough";
import { COLORS } from "@/components/reports/reportTheme";
import { MessageSquare, AlertTriangle } from "lucide-react";

// =============== Tab F — Communications (expanded) ===============
export function CommsReport({ filters }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/communications", { params }).then((r) => setData(r.data));
  }, [JSON.stringify(filters)]); // eslint-disable-line

  if (!data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="comms-report">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email success</div>
              <MessageSquare className="w-4 h-4 text-indigo-600" />
            </div>
            <div className={`font-heading text-2xl font-bold mt-1 ${(data.kpi.email_success_pct || 0) >= 95 ? "text-emerald-700" : "text-amber-700"}`}>
              {data.kpi.email_success_pct != null ? `${data.kpi.email_success_pct}%` : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {data.email_summary.sent}/{data.email_summary.total} sent
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">WhatsApp success</div>
              <MessageSquare className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-emerald-700">
              {data.kpi.whatsapp_success_pct != null ? `${data.kpi.whatsapp_success_pct}%` : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {data.whatsapp_summary.sent}/{data.whatsapp_summary.total} sent
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total messages</div>
              <MessageSquare className="w-4 h-4 text-slate-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-slate-800">{data.kpi.total_messages}</div>
            <div className="text-[11px] text-slate-500 mt-1">email + WhatsApp</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">WhatsApp 24h-window util</div>
              <MessageSquare className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-indigo-700">
              {data.kpi.wa_window_util_pct != null ? `${data.kpi.wa_window_util_pct}%` : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">tickets engaged inside 24h</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="email-by-day-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Email volume per day</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.email_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sent" stackId="a" fill="#059669" name="Sent" />
                <Bar dataKey="failed" stackId="a" fill="#e11d48" name="Failed" />
                <Bar dataKey="skipped_no_poc" stackId="a" fill="#d97706" name="Skipped (no POC)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="wa-by-day-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">WhatsApp volume per day</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.whatsapp_by_day.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No WhatsApp activity yet</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.whatsapp_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sent" stackId="a" fill="#10b981" name="Sent" />
                  <Bar dataKey="skipped" stackId="a" fill="#d97706" name="Skipped" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="poc-fanout-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">POC fan-out per department</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.poc_fanout.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No fan-out yet</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.poc_fanout.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis dataKey="dept" type="category" tick={{ fontSize: 10, fill: "#64748b" }} width={130} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="to_count" fill="#4f46e5" name="To recipients" />
                  <Bar dataKey="cc_count" fill="#0ea5e9" name="CC recipients" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="top-failures-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-rose-600" />Top failure reasons
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {data.top_failure_reasons.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No failures recorded — clean delivery.</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.top_failure_reasons} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis dataKey="reason" type="category" tick={{ fontSize: 9, fill: "#64748b" }} width={200} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#e11d48" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {data.template_usage.length > 0 && (
        <Card className="border-slate-200" data-testid="template-usage-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">WhatsApp template usage</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.template_usage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="template" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sent" fill="#10b981" name="Sent" />
                <Bar dataKey="skipped" fill="#d97706" name="Skipped" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
